import { memo } from 'react';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { computeDefenseLeftAtHome, type HomeDefenseComposition } from '../../utils/homeDefense';
import type { Province } from '../../types/province';

interface DefenseLeftAtHomeCardProps {
  province: Province;
  composition: HomeDefenseComposition;
}

const ZERO_COMPOSITION: HomeDefenseComposition = { soldiers: 0, elites: 0 };

/**
 * SF-174 Component 6: sibling card to `OwnOffenseCard` — the DP the attacker's home is left with
 * after this composition ships out. A genuine what-if recompute (troops haven't shipped yet, so
 * it can't be read off `province.military.defensePoints`); see `homeDefense.ts` for the formula
 * and its documented divergences from the live engine.
 */
export const DefenseLeftAtHomeCard = memo(function DefenseLeftAtHomeCard({
  province,
  composition,
}: DefenseLeftAtHomeCardProps) {
  const after = computeDefenseLeftAtHome(province, composition);
  const before = computeDefenseLeftAtHome(province, ZERO_COMPOSITION);

  // SF-183 Component 7: this forecast applies computeDefense's land floor, but Ambush's own
  // restricted chain (MilitarySystem.computeAtHomeAmbushDefense) is deliberately floorless — so
  // this card can show a reassuring nonzero forecast for a composition that would leave the
  // province at exactly 0 Ambush defense. Flag it: all four COMBAT_UNITS types (soldiers, defense
  // specialists, elites, offense specialists) at 0 at home after this send.
  const pop = province.population;
  const soldiersRemaining = Math.max(0, (pop.soldiersAtHome ?? pop.soldiers) - composition.soldiers);
  const elitesRemaining = Math.max(0, (pop.elitesAtHome ?? pop.elites) - composition.elites);
  const offenseSpecialistsRemaining = Math.max(
    0,
    (pop.offenseSpecialistsAtHome ?? pop.offenseSpecialists) - (composition.offenseSpecialists ?? 0)
  );
  const defenseSpecialistsAtHome = pop.defenseSpecialists; // never travels in an Army
  const undefendedAgainstAmbush =
    soldiersRemaining === 0
    && elitesRemaining === 0
    && offenseSpecialistsRemaining === 0
    && defenseSpecialistsAtHome === 0;

  return (
    <div
      className="rounded p-3"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.text.tertiary }}>
          DPA left at home
        </span>
        <span className="text-lg font-bold" style={{ color: COLORS.text.primary }}>
          {after.dpaLeftAtHome.toFixed(2)}
        </span>
      </div>
      <div className="text-xs" style={{ color: COLORS.text.muted }}>
        was {before.dpaLeftAtHome.toFixed(2)} before this send ({formatNumber(after.defenseLeftAtHome)} DP)
      </div>
      {undefendedAgainstAmbush && (
        <div className="text-xs mt-1" style={{ color: COLORS.warning }}>
          Undefended against Ambush — no combat units would remain at home
        </div>
      )}
    </div>
  );
});
